import type { ReactNode } from 'react';
import { Building2, ChevronsUpDown } from 'lucide-react';
import { FindItemBar } from './FindItemBar';
import { DocItemRow } from './DocItemRow';
import { useDocFilters } from './useDocFilters';
import type { DocListItem } from './types';

/**
 * A headed listing of documents as rows — the one component behind
 * `/docs/requirements`, `/docs/components` and `/docs/design`, and reusable by
 * the Build console's Standards/Validate lists. It owns the shared shape (title
 * header, "Find item" box, an org select that self-hides at ≤ 1 org, the rows,
 * a no-match line); everything surface-specific arrives as data or slots:
 *
 * - `items`  the rows, already mapped by the caller (specs, repos, …)
 * - `headerActions`  right-aligned header slot (e.g. a "New" button)
 * - `banner`  a strip above the list (repo config, publish bar)
 * - `emptyState`  shown when there are no items at all (before filtering)
 * - `footer`  below the list (e.g. "repositories without documentation")
 *
 * It renders no document *body* — a listing is discovery-only; opening a row is
 * what shows content. Routing and data access stay with the caller via
 * `item.onOpen`, so this file imports neither.
 */
export interface DocListSectionProps {
  title: string;
  subtitle: string;
  items: DocListItem[];
  headerActions?: ReactNode;
  banner?: ReactNode;
  loading?: boolean;
  /** Rendered when `items` is empty (distinct from "no search match"). */
  emptyState?: ReactNode;
  /** No-search-match line, e.g. "No requirements match your search." */
  noMatchLabel?: string;
  footer?: ReactNode;
  /** Placeholder for the search box. */
  searchPlaceholder?: string;
  /** aria-label for the org select. */
  orgLabel?: string;
}

export function DocListSection({
  title,
  subtitle,
  items,
  headerActions,
  banner,
  loading = false,
  emptyState,
  noMatchLabel = 'No items match your search.',
  footer,
  searchPlaceholder,
  orgLabel = 'Filter by organization',
}: DocListSectionProps) {
  const { query, setQuery, org, setOrg, orgs, filtered } = useDocFilters(items);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
      {/* Shared section header: title over subtitle, right-aligned action slot. */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{title}</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>
        </div>
        {headerActions && <div className="shrink-0 flex items-center gap-3 mt-1">{headerActions}</div>}
      </div>

      {banner}

      {loading ? (
        <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
      ) : items.length === 0 ? (
        emptyState ?? null
      ) : (
        <>
          <FindItemBar value={query} onChange={setQuery} placeholder={searchPlaceholder}>
            {orgs.length > 1 && (
              <div className="relative">
                <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                <select
                  value={org}
                  onChange={(e) => setOrg(e.target.value)}
                  aria-label={orgLabel}
                  className="pl-8 pr-8 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 appearance-none cursor-pointer"
                >
                  <option value="all">All organizations</option>
                  {orgs.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <ChevronsUpDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              </div>
            )}
          </FindItemBar>
          {filtered.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500 dark:text-slate-400">
              {noMatchLabel}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((item) => (
                <DocItemRow
                  key={item.id}
                  icon={item.icon}
                  title={item.title}
                  location={item.location}
                  date={item.date}
                  dateLabel={item.dateLabel}
                  status={item.status}
                  onOpen={item.onOpen}
                  openTitle={item.openTitle}
                />
              ))}
            </div>
          )}
          {footer}
        </>
      )}
    </div>
  );
}
